<template>
  <div v-if="hasAnyPermission('workflows-view')" class="py-4 container-fluid">
    <ToastPrime position="bottom-left" />
    <div class="card">
      <div class="card-header py-3">
        <div class="row align-items-center">
          <!-- Bagian H3 di Kiri -->
          <div class="col-4">
            <h5 class="mb-0">Workflow</h5>
          </div>

          <!-- Bagian Pencarian di Tengah -->
          <div class="col-4 d-flex justify-content-center">
            <IconField class="w-100">
              <InputIcon class="pi pi-search" />
              <InputText v-model="table.search" type="text" size="small" placeholder="Search Characteristic"
                class="rounded-pill w-100" @keyup="filter" />
            </IconField>
          </div>

          <!-- Bagian Button di Kanan -->
          <div class="col-4 text-end">
            <button v-if="hasAnyPermission('workflows-create')" type="button" class="mx-1 mb-0 btn btn-sm btn-primary"
              @click="openAddModal">Add</button>
          </div>
        </div>
      </div>
      <div style="height: 500px; overflow-y: auto">
        <DataTable :value="table.data" removable-sort scrollable scroll-height="flex" table-style="min-width: 100rem">
          <Column header="Workflow Code" header-class="bg-light" :header-style="columnWidth(90)"
            :body-style="columnWidth(90)" sortable>
            <template #body="row">
              <Skeleton v-if="table.onLoad" />
              <span v-else>{{ row.data.code }}</span>
            </template>
          </Column>
          <Column header="Workflow Type" header-class="bg-light" :header-style="columnWidth(110)"
            :body-style="columnWidth(110)" sortable>
            <template #body="row">
              <Skeleton v-if="table.onLoad" />
              <span v-else>{{ row.data.description }}</span>
            </template>
          </Column>
          <Column header="Source Type" header-class="bg-light" :header-style="columnWidth(110)"
            :body-style="columnWidth(110)" sortable>
            <template #body="row">
              <Skeleton v-if="table.onLoad" />
              <span v-else>{{ row.data.source_type }}</span>
            </template>
          </Column>
          <Column header="Source" header-class="bg-light" :header-style="columnWidth(150)"
            :body-style="columnWidth(150)" sortable>
            <template #body="row">
              <Skeleton v-if="table.onLoad" />
              <span v-else>
                <div class="line-clamp-2">{{ row.data.source?.target_model }}</div>
              </span>
            </template>
          </Column>
          <Column header="Status" header-class="bg-light" :header-style="columnWidth(150)"
            :body-style="columnWidth(150)" sortable>
            <template #body="row">
              <Skeleton v-if="table.onLoad" />
              <span v-else>{{ row.data.status === "active" ? "Active" : (row.data.status === "inactive") ? "Inactive" :
                "" }}</span>
            </template>
          </Column>
          <Column header="" header-class="bg-light" :header-style="columnWidth(150)" :body-style="columnWidth(150)">
            <template #body="row">
              <div v-if="row.data?.description">
                <ButtonPrime v-if="hasAnyPermission('workflows-update')" v-slot="slotProps" v-tooltip.bottom="'Update'"
                  as-child outlined rounded size="small">
                  <RouterLink :to="{ name: 'Edit Workflow', params: { id: row.data.id } }" class="p-2"
                    :class="slotProps.class"><i class="pi pi-pencil"></i></RouterLink>
                </ButtonPrime>
                <ButtonPrime v-if="hasAnyPermission('workflows-delete')" v-tooltip.bottom="'Delete'" icon="pi pi-trash"
                  outlined rounded severity="danger" size="small" style="margin-left: 5px;"
                  @click="deleteItem(row.data.id)" />
              </div>
              <div v-else class="m-4"><span /></div>
            </template>
          </Column>
          <template #empty>
            <div style="text-align: center; padding: 35px 0;">
              <img src="/no_data.png" alt="No Data" style="width: 330px; height: 330px; opacity: 0.7;" />
              <p class="text-muted mt-2">No items found</p>
            </div>
          </template>
        </DataTable>
      </div>
      <div class="card-footer pb-0 d-flex justify-content-between align-items-center">
        <BasePagination v-model="table.page.current_page" :page-count="table.page.total_page" size="sm"
          @click="changePage()" />
        <p>
          {{ (table.page.current_page - 1) * table.page.per_page + table.data.length }}
          of {{ table.total }}
        </p>
      </div>
    </div>

    <DialogPrime v-model:visible="modal.visible" :header="modal.title" style="width: 45rem; margin: 10px;">
      <div class="mb-2">
        <label for="description_modal" class="font-semibold">Workflow <span class="text-danger">*</span></label>
        <InputText id="description_modal" v-model="modal.data.description" placeholder="Description"
          :class="`${(modal.errorMessage['description'] && modal.data.description == '') ? 'border-danger' : ''}`" fluid
          @input="toUpperCaseVal($event, 'description')" />
        <small v-if="modal.errorMessage['description'] && modal.data.description == ''" class="text-danger">{{
          modal.errorMessage['description'] }}</small>
      </div>

      <div class="mb-2">
        <label for="status_modal" class="font-semibold">Status <span class="text-danger">*</span></label>
        <SelectPrime id="status_modal" v-model="modal.data.status" :options="statusOpts" option-label="label"
          option-value="value" placeholder="Please choose status"
          :invalid="modal.errorMessage['status'] && modal.data.status === ''" class="w-full md:w-56" fluid />
        <small v-if="modal.errorMessage['status'] && modal.data.status == ''" class="text-danger">{{
          modal.errorMessage['status'] }}</small>
      </div>

      <div class="mb-2" style="width: 100%;">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <label for="source_modal" class="font-semibold mb-0"> Source <span class="text-danger">*</span></label>
          <ButtonPrime v-if="modal.data.source" type="button" label="Beautify JSON" icon="pi pi-code" size="small" text
            @click="beautifyJson" />
        </div>
        <PrimeTextarea id="source_modal" v-model="modal.data.source" placeholder="Enter JSON Source..." fluid
          :class="`${(modal.errorMessage['source'] && !modal.data.source) ? 'border-danger' : ''}`" rows="12"
          style="font-family: monospace; font-size: 12px;" />
        <small v-if="modal.errorMessage['source']" class="text-danger">{{
          modal.errorMessage['source'] }}</small>
      </div>

      <template #footer>
        <div class="d-flex justify-content-end gap-2 pt-3 border-top w-100 mt-2 px-1">
          <ButtonPrime type="button" label="Cancel" severity="danger" class="px-4 border-0" raised size="small"
            :disabled="modal.onLoad" @click="modal.visible = false"></ButtonPrime>
          <ButtonPrime type="button" severity="contrast" raised size="small" label="Save" class="px-4"
            :disabled="modal.onLoad" @click="save()">
            <template v-if="modal.onLoading" #icon>
              <i class="fas fa-spinner fa-spin me-2"></i>
            </template>
          </ButtonPrime>
        </div>
      </template>
    </DialogPrime>
  </div>
  <NoAccess v-else />
</template>

<script>
import { mapState } from "vuex";
import NoAccess from "@/components/NoAccess.vue";
import api from "@/helpers/api";
import workflowService from "@/services/generalSettings/workflowService"

export default {
  components: {
    NoAccess,
  },
  data() {
    return {
      table: {
        search: this.$route.query.description ? this.$route.query.description : "",
        onLoad: false,
        total: 3,
        data: [{}],
        page: {
          current_page: 1,
          total_page: 1,
        },
        limit: 100,
      },
      modal: {
        visible: false,
        action: "",
        title: "",
        data: {},
        errorMessage: {},
        onLoad: false,
      },
      statusOpts: [{ label: "Active", value: "active" }, { label: "Inactive", value: "inactive" }],
      sourceTypeOpt: [{ label: "Query", value: "query" }],
    };
  },
  computed: {
    ...mapState({
      authUserPermissions: (state) => state.auth.userHasPermissions || [],
    }),
    filledTableData() {
      const visibleRows = this.table.data
      const dummyCount = Math.max(0, 7 - visibleRows.length)
      const dummies = Array.from({ length: dummyCount }, () => ({ __isDummy: true }))
      return [...visibleRows, ...dummies]
    },
    anyEmptyVal() {
      return (!this.modal.data.description || !this.modal.data.source_type || !this.modal.data.source || !this.modal.data.status);
    }
  },
  mounted() {
    if (this.hasAnyPermission('workflows-view')) {
      this.get()
    }
  },
  methods: {
    hasAnyPermission(...perms) {
      return perms.some((p) => this.authUserPermissions.includes(p));
    },
    async get() {
      this.table.onLoad = true
      try {
        const response = await api(this, workflowService.list({ limit: this.table.limit, page: this.table.page.current_page, description: this.table.search }), false);

        this.table.data = response.data.data;
        this.table.total = response.data.total;
        this.table.page = response.data;
        this.table.page.total_page = response.data.last_page
        // console.log("page", this.table.page);
      } catch (error) {
        this.table.data = []
        this.table.total = 0
      } finally {
        this.table.onLoad = false
      }
    },
    openAddModal() {
      this.modal.action = "create";
      this.modal.title = "Add New Workflow";
      this.modal.data = { description: "", source_type: "json", source: "", status: "active" };
      this.modal.errorMessage = {};
      this.modal.onLoad = false;
      this.modal.visible = true;
    },
    openEditModal(item) {
      this.modal.action = "edit";
      this.modal.title = "Edit Workflow";
      this.modal.data = { ...item };
      this.modal.errorMessage = {};
      this.modal.onLoad = false;
      this.modal.visible = true;

      // Auto-format JSON if possible
      if (this.modal.data.source) {
        try {
          const obj = typeof this.modal.data.source === 'string'
            ? JSON.parse(this.modal.data.source)
            : this.modal.data.source;
          this.modal.data.source = JSON.stringify(obj, null, 2);
        } catch (e) {
          // Keep as is if not valid JSON
        }
      }
    },
    beautifyJson() {
      try {
        const obj = JSON.parse(this.modal.data.source);
        this.modal.data.source = JSON.stringify(obj, null, 2);
        delete this.modal.errorMessage['source'];
      } catch (e) {
        this.$toastNotifPrime('error', "Invalid JSON", "Could not format the source because it's not a valid JSON string");
      }
    },
    save() {
      let isValid = true;
      const requiredField = [{ key: 'source', message: 'Source' }, { key: 'source_type', message: 'Source Type' }, { key: 'status', message: 'Status' }, { key: 'description', message: 'Workflow Type' }];
      requiredField.forEach((val) => {
        if (!this.modal.data[val.key]) {
          this.modal.errorMessage[val.key] = `${val.message} is required`;
          isValid = false;
        }
      })

      // JSON Validation
      if (isValid && this.modal.data.source) {
        try {
          JSON.parse(this.modal.data.source);
        } catch (e) {
          this.modal.errorMessage['source'] = "Source must be a valid JSON string";
          isValid = false;
        }
      }

      if (!isValid) {
        this.$toastNotifPrime('error', "Validation failed", "There fields have not been filled or contain invalid data")
        return false
      };

      this.modal.onLoad = true
      setTimeout(async () => {
        try {
          if (this.modal.action === 'create') {
            await api(this, workflowService.create(this.modal.data));
          } else if (this.modal.action === 'edit') {
            delete this.modal.data.code;
            await api(this, workflowService.update(this.modal.data.id, this.modal.data))
          }
          this.modal.data = {};
          this.modal.visible = false;
          this.get();
        } catch (error) {
          this.handleErrorObjectNested(error)
        } finally {
          this.modal.onLoad = false
        }
      }, 500)
    },
    deleteItem(id) {
      this.confirmDialog("You won't be able to revert this!", "delete it").then((result) => {
        if (result.isConfirmed) {
          api(this, workflowService.delete(id)).then(() => {
            this.get()
          }).catch(() => { });
        }
      });
    },
    filter() {
      if (this.table.search != this.$route.query.characteristic) {
        setTimeout(() => {
          if (this.table.search.length > 3) {
            this.$router.push({ name: 'Workflow', params: { moduleSlug: 'material-management' }, query: { description: this.table.search } });
          } else {
            this.$router.push({ name: 'Workflow', params: { moduleSlug: 'material-management' } });
          }
          this.get();
        }, 500)
      }
    },
    changePage() {
      this.table.onLoad = true;
      setTimeout(() => {
        this.get();
      }, 100);
    },
    toUpperCaseVal(event, key) {
      const input = event.target;
      const start = input.selectionStart;
      const end = input.selectionEnd;

      const upper = input.value.toUpperCase();
      this.modal.data[key] = upper;

      this.$nextTick(() => {
        input.setSelectionRange(start, end); //agar kursor berlanjut di mana huruf diketik
      });
    },
    handleErrorObjectNested(error) {
      if (error.response?.data?.code == 400 || error.response?.status == 422 || error.response?.status == 500) {
        const errorData = error.response?.data?.data;

        if (typeof errorData === 'object' && errorData !== null) {
          for (const key in errorData) {
            if (Object.prototype.hasOwnProperty.call(errorData, key)) {
              const match = key.match(/^items\.(\d+)\.(.+)$/);
              if (match) {
                error.response.data.data[key].forEach(message => {
                  this.$toastNotifPrime('error', error.response.data.message, `${message} in row ${parseInt(match[1]) + 1}`)
                })
              } else {
                this.$toastNotifPrime('error', error.response.data.message, errorData[key]);
                // errorData[key].forEach(message => {
                //   this.$toastNotifPrime('error', error.response.data.message, message);
                // });
              }
            }
          }
        }
      }
    },
    columnWidth(value) {
      return { width: `${value}px`, minWidth: `${value}px` };
    },
  },
};
</script>
